
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import CustomErrorPage from '@/pages/guest/CustomErrorPage';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to monitoring service in production
    if (import.meta.env.PROD) {
      this.logErrorToService(error, errorInfo);
    }
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // This would integrate with your monitoring service
    const errorData = {
      message: error instanceof Error ? error.message : "An unknown error occurred",
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };
    
    console.error('Production Error:', errorData);
    // TODO: Send to monitoring service (e.g., Sentry, LogRocket)
  };

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Use our custom conversion-focused error page
      return (
        <>
          <CustomErrorPage
            title="Oops! Something Sweet Went Wrong"
            message="Technical hiccup in the kitchen! But don't worry, our doughnuts are still perfect"
            showRetry={true}
            onRetry={this.handleRetry}
          />

          {/* Dev error details */}
          {import.meta.env.DEV && this.state.error && (
            <div className="fixed bottom-4 right-4 max-w-md">
              <details className="p-3 bg-red-900 text-white rounded text-xs shadow-lg">
                <summary className="cursor-pointer font-medium">🚨 Dev Error Details</summary>
                <pre className="mt-2 whitespace-pre-wrap text-xs overflow-auto max-h-40">
                  {this.state.error.stack}
                </pre>
              </details>
            </div>
          )}
        </>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
